import { useState } from 'react'
import { Mail, Lock, LogIn, UserPlus } from 'lucide-react'
import { supabase } from '../lib/supabase'
import { buildPasswordResetRedirectTo } from '../lib/authRecovery'

type Mode = 'signin' | 'signup'

export function LoginPage() {
  const [mode, setMode] = useState<Mode>('signin')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [name, setName] = useState('')
  const [loading, setLoading] = useState(false)
  const [resetLoading, setResetLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [info, setInfo] = useState<string | null>(null)

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    setInfo(null)
    setLoading(true)

    try {
      if (mode === 'signin') {
        const { error } = await supabase.auth.signInWithPassword({ email, password })
        if (error) throw error
        // 成功時は App.tsx 側で /login → / にリダイレクトされる
      } else {
        const { error } = await supabase.auth.signUp({
          email,
          password,
          options: { data: { name } }
        })
        if (error) throw error
        setInfo(
          'アカウント登録メールを送りました。受信箱の確認リンクをクリックしてください。'
        )
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setLoading(false)
    }
  }

  async function onResetPassword() {
    setError(null)
    setInfo(null)

    if (!email.trim()) {
      setError('パスワード再設定メールを送るメールアドレスを入力してください。')
      return
    }

    setResetLoading(true)
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
        redirectTo: buildPasswordResetRedirectTo(window.location.href)
      })
      if (error) throw error
      setInfo('パスワード再設定メールを送りました。メール内のリンクから再設定してください。')
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setResetLoading(false)
    }
  }

  return (
    <main className="min-h-full flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-sm bg-white rounded-2xl shadow-sm border border-gray-200 p-8">
        <div className="flex flex-col items-center mb-6">
          <img src="/icon.png" alt="LogMane" className="w-14 h-14 mb-3" />
          <h1 className="text-xl font-bold text-gray-800">LogMane</h1>
          <p className="text-xs text-gray-500 mt-1">ライバー事務所マネジメント基盤</p>
        </div>

        <div className="flex gap-1 bg-gray-100 rounded-lg p-1 mb-6">
          <button
            type="button"
            onClick={() => {
              setMode('signin')
              setError(null)
              setInfo(null)
            }}
            className={`flex-1 py-2 text-sm rounded-md transition-colors ${
              mode === 'signin'
                ? 'bg-white text-gray-800 shadow-sm font-medium'
                : 'text-gray-500'
            }`}
          >
            ログイン
          </button>
          <button
            type="button"
            onClick={() => {
              setMode('signup')
              setError(null)
              setInfo(null)
            }}
            className={`flex-1 py-2 text-sm rounded-md transition-colors ${
              mode === 'signup'
                ? 'bg-white text-gray-800 shadow-sm font-medium'
                : 'text-gray-500'
            }`}
          >
            新規登録
          </button>
        </div>

        <form onSubmit={onSubmit} className="space-y-3">
          {mode === 'signup' && (
            <div>
              <label className="block text-xs text-gray-600 mb-1">氏名</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                placeholder="山田 太郎"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-blue-400"
              />
            </div>
          )}
          <div>
            <label className="block text-xs text-gray-600 mb-1">メールアドレス</label>
            <div className="relative">
              <Mail className="w-4 h-4 absolute top-2.5 left-3 text-gray-400" />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                placeholder="you@example.com"
                className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-blue-400"
              />
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="block text-xs text-gray-600">パスワード</label>
              {mode === 'signin' && (
                <button
                  type="button"
                  onClick={onResetPassword}
                  disabled={loading || resetLoading}
                  className="text-xs text-blue-600 hover:text-blue-700 disabled:opacity-50"
                >
                  {resetLoading ? '送信中…' : 'パスワードを忘れた方'}
                </button>
              )}
            </div>
            <div className="relative">
              <Lock className="w-4 h-4 absolute top-2.5 left-3 text-gray-400" />
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete={mode === 'signin' ? 'current-password' : 'new-password'}
                minLength={8}
                placeholder="8文字以上"
                className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-blue-400"
              />
            </div>
          </div>

          {error && (
            <div className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">
              {error}
            </div>
          )}
          {info && (
            <div className="text-sm text-emerald-700 bg-emerald-50 rounded-lg px-3 py-2">
              {info}
            </div>
          )}

          <button
            type="submit"
            disabled={loading || resetLoading}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-gray-900 text-white rounded-lg text-sm font-medium hover:bg-gray-800 disabled:opacity-50"
          >
            {mode === 'signin' ? (
              <>
                <LogIn className="w-4 h-4" />
                {loading ? 'ログイン中…' : 'ログイン'}
              </>
            ) : (
              <>
                <UserPlus className="w-4 h-4" />
                {loading ? '登録中…' : 'アカウントを作る'}
              </>
            )}
          </button>
        </form>
      </div>
    </main>
  )
}
